import { combineReducers } from 'redux';
import { push } from 'react-router-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import message from 'antd/lib/message';
import { t } from '../../../app/i18n';
import { hideModal } from '../../../features/modal/ducks';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import loanApi, { apiCallIds } from '../api';
import { loanStatus } from '../../../common/enums';

/**
 * ACTION TYPES
 */
export const FETCH_LOANS = 'loanManagement/FETCH_LOANS';
export const FETCH_DISPOSE_LOANS = 'loanManagement/FETCH_DISPOSE_LOANS';
export const SET_LOANS_PAGINATION = 'loanManagement/SET_LOANS_PAGINATION';
export const SET_LOANS_FILTER = 'loanManagement/SET_LOANS_FILTER';
export const SET_DISPOSE_LOANS_FILTER = 'loanManagement/SET_DISPOSE_LOANS_FILTER';
export const FETCH_LOAN = 'loanManagement/FETCH_LOAN';
export const ACCEPT_LOAN = 'loanManagement/ACCEPT_LOAN';
export const REJECT_LOAN = 'loanManagement/REJECT_LOAN';
export const FETCH_LOAN_HISTORY = 'loanManagement/FETCH_LOAN_HISTORY';
export const FETCH_EARLY_REPAYMENT = 'loanManagement/FETCH_EARLY_REPAYMENT';
export const EARLY_REPAY_LOAN = 'loanManagement/EARLY_REPAY_LOAN';
export const CLOSE_EARLY_REPAY_LOAN_MODAL = 'loanManagement/CLOSE_EARLY_REPAY_LOAN_MODAL';
export const REJECT_LOANS = 'loanManagement/REJECT_LOANS';
export const SHIFT_PAYMENT = 'loanManagement/SHIFT_PAYMENT';
export const FETCH_LOAN_ALL_MESSAGES = 'loanManagement/FETCH_LOAN_ALL_MESSAGES';
export const UPDATE_LOAN_PROVISION_FEE = 'loanManagement/UPDATE_LOAN_PROVISION_FEE';
export const DISPOSE_LOANS = 'loans/DISPOSE_LOANS';
export const CLEAR_DISPOSED_AMOUNT = 'loans/CLEAR_DISPOSED_AMOUNT';
export const DISPOSE_LOAN = 'loans/DISPOSE_LOAN';
export const FETCH_EARLY_REPAYMENT_RECORD = 'loanManagement/FETCH_EARLY_REPAYMENT_RECORD';
export const RETURN_OVERPAY = 'loanManagement/RETURN_OVERPAY';
export const SET_PENALTY_SETTING = 'loanManagement/SET_PENALTY_SETTING';
export const SHIFT_INSTALLMENT_DATE = 'loanManagement/SHIFT_INSTALLMENT_DATE';
export const SAVE_NOTES = 'loanManagement/SAVE_NOTES';
export const FRAUD_CHECK = 'loanManagement/FRAUD_CHECK';
export const CANCEL_LOAN = 'loanManagement/CANCEL_LOAN';


/**
 * ACTIONS
 */
export const fetchLoansActions = createApiActionCreators(FETCH_LOANS);
export const fetchDisposeLoansActions = createApiActionCreators(FETCH_DISPOSE_LOANS);
export const setLoansPaginationAction = createActionCreator(SET_LOANS_PAGINATION);
export const setLoansFilterAction = createActionCreator(SET_LOANS_FILTER);
export const setDisposeLoansFilterAction = createActionCreator(SET_DISPOSE_LOANS_FILTER);
export const fetchLoanActions = createApiActionCreators(FETCH_LOAN);
export const acceptLoanActions = createApiActionCreators(ACCEPT_LOAN);
export const rejectLoanActions = createApiActionCreators(REJECT_LOAN);
export const fetchLoanHistoryActions = createApiActionCreators(FETCH_LOAN_HISTORY);
export const fetchEarlyRepaymentActions = createApiActionCreators(FETCH_EARLY_REPAYMENT);
export const earlyRepayLoanActions = createApiActionCreators(EARLY_REPAY_LOAN);
export const closeEarlyRepayLoanModal = createActionCreator(CLOSE_EARLY_REPAY_LOAN_MODAL);
export const rejectLoansActions = createApiActionCreators(REJECT_LOANS);
export const shiftPaymentActions = createApiActionCreators(SHIFT_PAYMENT);
export const fetchLoanAllMessagesActions = createApiActionCreators(FETCH_LOAN_ALL_MESSAGES);
export const updateLoanProvisionFeeActions = createApiActionCreators(UPDATE_LOAN_PROVISION_FEE);
export const disposeLoansActions = createApiActionCreators(DISPOSE_LOANS);
export const disposeLoanActions = createApiActionCreators(DISPOSE_LOAN);
export const clearDisposedAmountActions = createActionCreator(CLEAR_DISPOSED_AMOUNT);
export const fetchEarlyRepayRecordActions = createApiActionCreators(FETCH_EARLY_REPAYMENT_RECORD);
export const setPenaltySetting = createApiActionCreators(SET_PENALTY_SETTING);
export const shiftInstallmentDateActions = createApiActionCreators(SHIFT_INSTALLMENT_DATE);
export const returnOverpayActions = createApiActionCreators(RETURN_OVERPAY);
export const savePayrollDeductionsNotesActions = createApiActionCreators(SAVE_NOTES);
export const fraudCheckActions = createApiActionCreators(FRAUD_CHECK);
export const cancelLoanActions = createApiActionCreators(CANCEL_LOAN);

/**
 * REDUCERS
 */
const initialState = {
  loan: null,
  loans: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: loanStatus.POD,
    },
  },
  disposeLoans: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: loanStatus.POD,
    },
    disposedAmount: null,
  },
  loanHistory: null,
  earlyRepayment: null,
  earlyRepaymentRecord: null,
  shiftPayment: null,
  loanAllMessages: null,
  loanProvisionFee: null,
  returnOverpay: null,
  penaltySetting: {
    noPenalty: false,
    noPenaltyNotification: false
  }
};

const installments = {
  day: 0
}

const loan = createReducer(initialState.loan, {
  [FETCH_LOAN]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
  [ACCEPT_LOAN]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [REJECT_LOAN]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [EARLY_REPAY_LOAN]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [CANCEL_LOAN]: {
    [SUCCESS]: (state, payload) => payload,
  },
});

const loans = createReducer(initialState.loans, {
  [FETCH_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_LOANS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_LOANS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
});

const disposeLoans = createReducer(initialState.disposeLoans, {
  [FETCH_DISPOSE_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_LOANS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_DISPOSE_LOANS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
  [DISPOSE_LOANS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      disposedAmount: payload,
    }),
  },
  [DISPOSE_LOAN]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
    }),
  },
  [CLEAR_DISPOSED_AMOUNT]: (state) => ({
    ...state,
    disposedAmount: null,
  })
});

const loanHistory = createReducer(initialState.loanHistory, {
  [FETCH_LOAN_HISTORY]:{
    [SUCCESS]: (state, payload) => ({
      ...state,
      [payload.accountId]: {
        ...state && { ...state[payload.accountId] }, // if state exists, then spread
        [payload.historyStatus]: payload.loans
      }
    })
  }
});

const earlyRepayment = createReducer(initialState.earlyRepayment, {
  [FETCH_EARLY_REPAYMENT]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
  [CLOSE_EARLY_REPAY_LOAN_MODAL]: () => null,

});

const earlyRepaymentRecord = createReducer(initialState.earlyRepaymentRecord, {
  [FETCH_EARLY_REPAYMENT_RECORD]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  }
});

const shiftPayment = createReducer(initialState.shiftPayment, {
  [SHIFT_PAYMENT]: {
    [SUCCESS]: (state, payload) => payload,
  }
});

const loanAllMessages = createReducer(initialState.loanAllMessages, {
  [FETCH_LOAN_ALL_MESSAGES]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
});

const loanProvisionFee = createReducer(initialState.loanProvisionFee, {
  [UPDATE_LOAN_PROVISION_FEE]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
});

const loanPenaltySetting = createReducer(initialState.penaltySetting, {
  [SET_PENALTY_SETTING]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null
  }
});


const loanReturnOverpay = createReducer(initialState.returnOverpay, {
  [RETURN_OVERPAY]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null
  }
});

const shiftInstallment = createReducer(installments.day, {
  [SHIFT_INSTALLMENT_DATE]: {
    [SUCCESS]: (state, payload) => payload,
  }
});

const payrollDeductionsNotes = createReducer(installments.day, {
  [SAVE_NOTES]: {
    [SUCCESS]: (state, payload) => payload,
  }
});

export default combineReducers({
  loan,
  loans,
  disposeLoans,
  loanHistory,
  earlyRepayment,
  earlyRepaymentRecord,
  shiftPayment,
  loanAllMessages,
  loanProvisionFee,
  loanPenaltySetting,
  loanReturnOverpay,
  shiftInstallment,
  payrollDeductionsNotes
});

/**
 * SELECTORS
 */
export const selectLoanState = state => state.loan;
export const selectLoan = state => selectLoanState(state).loan;
export const selectLoansState = state => selectLoanState(state).loans;
export const selectDisposeLoansState = state => selectLoanState(state).disposeLoans;
export const selectLoans = state => selectLoansState(state).items;
export const selectDisposeLoans = state => selectDisposeLoansState(state).items;
export const selectDisposedAmount = state => selectDisposeLoansState(state).disposedAmount;
export const selectLoansPagination = state => selectLoansState(state).pagination;
export const selectLoansFilter = state => selectLoansState(state).filter;
export const selectDisposeLoansFilter = state => selectDisposeLoansState(state).filter
export const selectLoanHistory = (state, accountId, historyStatus) =>
  selectLoanState(state).loanHistory && selectLoanState(state).loanHistory[accountId] ? selectLoanState(state).loanHistory[accountId][historyStatus] : undefined;
export const selectEarlyRepayment = state => selectLoanState(state).earlyRepayment;
export const selectShift = state => selectLoanState(state).shiftPayment;
export const selectLoanAllMessages = state => selectLoanState(state);
export const selectLoanProvisionFee = state => selectLoanState(state);
export const selectEarlyRepaymentRecord = state => selectLoanState(state).earlyRepaymentRecord;

/**
 * SAGAS
 */
function* fetchLoan({ payload }) {
  const resp = yield call(loanApi.fetchLoan, payload.loanId);

  if (resp.ok) {
    yield put(fetchLoanActions.success(resp.data));
  } else {
    yield put(fetchLoanActions.failure(resp.error));
  }
}

function* fetchLoans({ payload }) {
  const resp = yield call(loanApi.fetchLoans, payload);

  if (resp.ok) {
    yield put(fetchLoansActions.success(resp.data));
    yield put(setLoansPaginationAction(resp.data));
  } else {
    yield put(fetchLoansActions.failure(resp.error));
  }
}

function* fetchDisposeLoans({ payload }) {
  const resp = yield call(loanApi.fetchDisposeLoans, payload);

  if (resp.ok) {
    yield put(fetchDisposeLoansActions.success(resp.data));
    yield put(setLoansPaginationAction(resp.data));
  } else {
    yield put(fetchDisposeLoansActions.failure(resp.error));
  }
}

function* disposeLoanList({ payload }) {
  const resp = yield call(loanApi.disposeLoan, payload.body);
  console.log(payload.allLoans)
  if (resp.ok) {
    yield put(disposeLoansActions.success(resp.data));
    yield put(fetchDisposeLoansActions.request({
      search: '',
      allLoans: payload.allLoans
    }));
  } else {
    yield put(disposeLoansActions.failure(resp.error));
  }
}


function* disposeLoan({ payload }) {
  const resp = yield call(loanApi.disposeLoan, payload.body);

  if (resp.ok) {
    yield put(disposeLoanActions.success(resp.data));
    message.success(`${t('Pohľadávka odpredaná')}.`, 5);
    yield put(fetchLoanActions.request(payload));
  } else {
    yield put(disposeLoanActions.failure(resp.error));
  }
}

function* acceptLoan({ payload }) {
  const resp = yield call(loanApi.acceptLoan, payload.loanId);

  if (resp.ok) {
    yield put(acceptLoanActions.success(resp.data));
  } else {
    yield put(acceptLoanActions.failure(resp.error));
  }
}

function* rejectLoan({ payload }) {
  const { loanId, rejectReason } = payload;
  const resp = yield call(loanApi.rejectLoan, loanId, { rejectReason });

  if (resp.ok) {
    yield put(rejectLoanActions.success(resp.data));
    yield put(hideModal(apiCallIds.REJECT_LOAN));
  } else {
    yield put(rejectLoanActions.failure(resp.error));
  }
}

function* fetchLoanHistory({ payload }) {
  const resp = yield call(loanApi.fetchLoanHistory, payload);

  if (resp.ok) {
    yield put(fetchLoanHistoryActions.success({
      accountId: payload.accountId,
      historyStatus: payload.historyStatus,
      loans: resp.data,
    }));
  } else {
    yield put(fetchLoanHistoryActions.failure(resp.error));
  }
}

function* fetchEarlyRepaymentAmount({ payload }) {
  const resp = yield call(loanApi.fetchEarlyRepaymentAmount, payload);

  if (resp.ok) {
    yield put(fetchEarlyRepaymentActions.success({
      ...resp.data,
      date: payload.date
    }));
  } else {
    yield put(fetchEarlyRepaymentActions.failure(resp.error));
  }
}

function* fetchEarlyRepayRecord({ payload }){
  const { loanId } = payload
  const resp = yield call(loanApi.fetchEarlyRepaymentRecord, loanId);

  if(resp.ok) {
    yield put(fetchEarlyRepayRecordActions.success(resp.data))
  }
  else{
    yield put(fetchEarlyRepayRecordActions.failure(resp.error))
  }
}

function* earlyRepayLoan({ payload }) {
  const { loanId, date } = payload;
  const resp = yield call(loanApi.earlyRepayLoan, loanId, date);

  if (resp.ok) {
    yield put(earlyRepayLoanActions.success(resp.data));
    message.success(`${t('Predčasné splatenie bolo zrealizované. Platby a splátky pôžičky budu spracované automaticky v priebehu niekoľkých minút.')}.`, 5);
    yield put(fetchLoanActions.request(payload));
  } else {
    yield put(earlyRepayLoanActions.failure(resp.error));
  }
}

function* rejectLoans({ payload }) {
  const resp = yield call(loanApi.rejectLoans);

  if (resp.ok) {
    yield put(rejectLoansActions.success(resp.data));
    message.success(`${t('Zamietnutých bolo')} ${resp.data.rejected} ${t('pôžičiek')}.`, 5);
    yield put(fetchLoansActions.request(payload));
  } else {
    yield put(rejectLoansActions.failure(resp.error));
  }
}

function* shiftPaymentLoan({ payload }) {
  const { loanId, data } = payload;
  const resp = yield call(loanApi.shiftPayment, payload);

  if (resp.ok) {
    yield put(shiftPaymentActions.success(resp.data));
    yield put(fetchLoanActions.request(payload));
  }
}

function* fetchLoanAllMessages({ payload }) {
  const { loanId } = payload;
  const resp = yield call(loanApi.fetchLoanAllMessages, loanId);
  if (resp.ok) {
    yield put(fetchLoanAllMessagesActions.success(resp.data));
  } else {
    yield put(fetchLoanAllMessagesActions.failure(resp.error));
  }
}

function* updateLoanProvisionFee({ payload }) {
  const { loanId, ProvisionFee } = payload;
  const resp = yield call(loanApi.updateLoanProvisionFee, loanId, ProvisionFee);
  if (resp.ok) {
    yield put(updateLoanProvisionFeeActions.success(resp.data));
  } else {
    yield put(updateLoanProvisionFeeActions.failure(resp.error));
  }
}

function* setPenaltySettings({ payload }) {
  const { loanId, values } = payload;
  const resp = yield call(loanApi.setPenaltySetting, loanId, values);
  if(resp.ok){
    yield put(setPenaltySetting.success(resp.data));
    yield put(fetchLoanActions.request({ loanId: loanId }))
  }
  else{
    yield put(setPenaltySetting.failure(resp.error));
  }
}

function* shiftInstallmentDate({ payload }) {

    const { loanId, date } = payload;
    const resp = yield call(loanApi.shiftInstallmentDate, loanId, date)
    if (resp.ok) {
      yield put(shiftInstallmentDateActions.success(resp.data))
      yield put(fetchLoanActions.request({ loanId: loanId }))
    }
    else {
      yield put(shiftInstallmentDateActions.failure(resp.error))
    }
}

function* savePayrollDeductionsNotes({ payload }) {

    const { loanId, notes } = payload;
    const resp = yield call(loanApi.savePayrollDeductionsNotes, loanId, notes)
    if (resp.ok) {
      yield put(savePayrollDeductionsNotesActions.success(resp.data))
      yield put(fetchLoanActions.request({ loanId: loanId }))
    }
    else {
      yield put(savePayrollDeductionsNotesActions.failure(resp.error))
    }
}



function* returnOverpay({ payload }){
  const { loanId, data } = payload;
  const resp = yield call(loanApi.returnOverpay, loanId, data);
  if (resp.ok) {
    yield put(returnOverpayActions.success(resp.data));
    message.success(`${t('Vrátenie preplatku prebehlo úspešne')}.`, 5);
    yield put(fetchLoanActions.request(payload));
  }
  else {
    yield put(returnOverpayActions.failure(resp.error))
  }
}

function* fraudCheck({payload}){
  const {loanId} = payload;
  const resp = yield call(loanApi.fraudCheck, loanId)
  if (resp.ok){
    yield put(fetchLoanActions.request(payload))
  }

}

function* cancelLoan({ payload }) {
  const { loanId } = payload;
  const resp = yield call(loanApi.cancelLoan, loanId);

  if (resp.ok) {
    yield put(cancelLoanActions.success(resp.data));
    message.success(t('Žiadosť bola úspešne zrušená.'), 5);
  } else {
    yield put(cancelLoanActions.failure(resp.error));
    message.error(t('Zrušenie žiadosti sa nepodarilo. Skúste to prosím neskôr.'), 5);
  }
}

export function* loanSaga() {
  yield takeLatest(createActionType(FETCH_LOAN, REQUEST), fetchLoan);
  yield takeLatest(createActionType(FETCH_LOANS, REQUEST), fetchLoans);
  yield takeLatest(createActionType(FETCH_DISPOSE_LOANS, REQUEST), fetchDisposeLoans);
  yield takeLatest(SET_LOANS_FILTER, fetchLoans);
  yield takeLatest(SET_DISPOSE_LOANS_FILTER, fetchDisposeLoans);
  yield takeLatest(createActionType(DISPOSE_LOANS, REQUEST), disposeLoanList);
  yield takeLatest(createActionType(DISPOSE_LOAN, REQUEST), disposeLoan);
  yield takeLatest(createActionType(ACCEPT_LOAN, REQUEST), acceptLoan);
  yield takeLatest(createActionType(REJECT_LOAN, REQUEST), rejectLoan);
  yield takeLatest(createActionType(FETCH_LOAN_HISTORY, REQUEST), fetchLoanHistory);
  yield takeLatest(createActionType(FETCH_EARLY_REPAYMENT, REQUEST), fetchEarlyRepaymentAmount);
  yield takeLatest(createActionType(EARLY_REPAY_LOAN, REQUEST), earlyRepayLoan);
  yield takeLatest(createActionType(REJECT_LOANS, REQUEST), rejectLoans);
  yield takeLatest(createActionType(SHIFT_PAYMENT, REQUEST), shiftPaymentLoan);
  yield takeLatest(createActionType(FETCH_LOAN_ALL_MESSAGES, REQUEST), fetchLoanAllMessages);
  yield takeLatest(createActionType(UPDATE_LOAN_PROVISION_FEE, REQUEST), updateLoanProvisionFee);
  yield takeLatest(createActionType(FETCH_EARLY_REPAYMENT_RECORD, REQUEST), fetchEarlyRepayRecord);
  yield takeLatest(createActionType(RETURN_OVERPAY, REQUEST), returnOverpay);
  yield takeLatest(createActionType(SET_PENALTY_SETTING, REQUEST), setPenaltySettings);
  yield takeLatest(createActionType(SHIFT_INSTALLMENT_DATE, REQUEST), shiftInstallmentDate);
  yield takeLatest(createActionType(SAVE_NOTES, REQUEST), savePayrollDeductionsNotes);
  yield takeLatest(createActionType(FRAUD_CHECK, REQUEST), fraudCheck);
  yield takeLatest(createActionType(CANCEL_LOAN, REQUEST), cancelLoan);
}



// WEBPACK FOOTER //
// ./src/features/loan-management/ducks/index.js